import { Controller, useForm, useFormContext } from 'react-hook-form';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AritoFormV2Context } from '@/components/custom/erp/form-v2/context';
import { ErpInputTableV2 } from '@/components/custom/erp/input-table-v2';
import { SearchFieldV2 } from '@/components/custom/erp/search-field-v2';
import type { FormFieldV2Option, FormFieldV2Props } from './types';
import { getFieldErrorMessage } from '../field-validation';
import { Checkbox } from '@/components/ui/checkbox';
import { FormDatePickerV2 } from './date-picker';
import { formatMoney } from '@/lib/formatUtils';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const baseInputClassName =
  'h-[var(--erp-control-height)] rounded-none border-0 border-b border-b-gray-200 bg-transparent px-2 text-[13px] shadow-none disabled:opacity-100 focus-visible:border-0 focus-visible:border-b focus-visible:border-b-main focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0';

const disabledFieldClassName = 'font-semibold text-black';
const errorFieldClassName = 'border-b-red-500 focus-visible:border-b-red-600';

const EMPTY_VAL = '___EMPTY_VALUE___';

const getOptionKey = (option: FormFieldV2Option, index: number) => {
  const val =
    option.value === undefined || option.value === null || option.value === '' ? EMPTY_VAL : String(option.value);
  return `${val}-${index}`;
};

interface SelectFieldV2Props {
  fieldId: string;
  field: any;
  options?: FormFieldV2Option[];
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  baseInputClassName?: string;
  disabledFieldClassName?: string;
  errorClassName?: string;
  invalid?: boolean;
  resolveOptionValue: (selectedValue: string) => any;
  onValueChange?: (value: any) => void;
}

const SelectFieldV2 = ({
  fieldId,
  field,
  options,
  disabled,
  className,
  placeholder,
  baseInputClassName,
  disabledFieldClassName,
  errorClassName,
  invalid = false,
  resolveOptionValue,
  onValueChange
}: SelectFieldV2Props) => {
  const getOptionValue = (val: any) => {
    return val === undefined || val === null || val === '' ? EMPTY_VAL : String(val);
  };

  const getRawValue = (val: string) => {
    return val === EMPTY_VAL ? '' : val;
  };

  const firstOptionValue = options && options.length > 0 ? options[0].value : '';
  const firstValue = getOptionValue(firstOptionValue);

  const normalizedSelectValue =
    field.value === undefined || field.value === null || field.value === '' ? firstValue : getOptionValue(field.value);

  useEffect(() => {
    if (options?.length && (field.value === undefined || field.value === null || field.value === '')) {
      const defaultValue = options[0].value;
      field.onChange(defaultValue);
      onValueChange?.(defaultValue as any);
    }
  }, [field.value, options, field, onValueChange]);

  return (
    <Select
      value={normalizedSelectValue}
      disabled={disabled}
      onValueChange={selectedValue => {
        const rawValue = getRawValue(selectedValue);
        const resolvedValue = resolveOptionValue(rawValue);
        field.onChange(resolvedValue);
        onValueChange?.(resolvedValue as any);
      }}
    >
      <SelectTrigger
        id={fieldId}
        aria-invalid={invalid}
        className={cn(
          baseInputClassName,
          'w-48 text-left font-normal focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-100',
          disabled && disabledFieldClassName,
          errorClassName,
          className
        )}
      >
        <SelectValue placeholder={placeholder || ''} />
      </SelectTrigger>
      <SelectContent className='z-[2000] max-h-[300px] rounded-sm'>
        {options?.map((option, index) => {
          const itemValue = getOptionValue(option.value);
          return (
            <SelectItem key={getOptionKey(option, index)} value={itemValue} className='py-1.5 text-[13px]'>
              {option.label}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

export const FormFieldV2 = ({
  label,
  value,
  control: controlProp,
  defaultValue,
  onValueChange,
  onBlur,
  name = '',
  type = 'text',
  disabled = false,
  options,
  error,
  className,
  fieldClassName,
  columns,
  searchEndpoint,
  searchResultValueKey,
  searchColumns,
  displayRelatedField,
  placeholder,
  labelClassName = 'shrink-0',
  transform
}: FormFieldV2Props<any>) => {
  const aritoFormContext = useContext(AritoFormV2Context);
  const hookFormContext = useFormContext();
  const fallbackForm = useForm({
    defaultValues: {
      [name]: defaultValue ?? value ?? (type === 'checkbox' ? false : '')
    }
  });
  const resolvedControl = controlProp ?? aritoFormContext?.control ?? hookFormContext?.control ?? fallbackForm.control;
  const errors = aritoFormContext?.errors ?? hookFormContext?.formState?.errors ?? fallbackForm.formState.errors;
  const fieldError = error || getFieldErrorMessage(errors, name);
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const parseNumber = (formattedValue: string): number | null => {
    if (!formattedValue) return null;
    const cleanValue = formattedValue.replace(/[^\d.-]/g, '');
    return parseFloat(cleanValue) || null;
  };

  useEffect(() => {
    if (type === 'number' && !isFocused) {
      const currentValue = value !== undefined ? value : '';
      setDisplayValue(currentValue !== undefined && currentValue !== null ? String(currentValue) : '');
    }
  }, [value, type, isFocused]);

  const searchDisplayField = useMemo(() => {
    if (searchResultValueKey && String(searchResultValueKey).trim()) {
      return searchResultValueKey;
    }

    const fallbackColumn = searchColumns?.find((column: NonNullable<typeof searchColumns>[number]) =>
      column.field?.trim()
    );
    return fallbackColumn?.field;
  }, [searchColumns, searchResultValueKey]);

  const fieldId = name;
  const resolveOptionValue = (selectedValue: string) => {
    const option = options?.find(item => String(item.value) === selectedValue);
    return option ? option.value : selectedValue;
  };
  return (
    <div>
      <div className={cn('flex items-center gap-3', className)}>
        {type !== 'checkbox' && !(type === 'table' || !label) && (
          <Label htmlFor={fieldId} className={cn('flex items-center text-left text-sm', labelClassName)}>
            {label}
          </Label>
        )}

        <div className={cn('min-w-0 flex-1', fieldClassName)}>
          <Controller
            name={name}
            control={resolvedControl}
            render={({ field }) => {
              if (searchEndpoint && searchEndpoint !== '') {
                return (
                  <SearchFieldV2<any>
                    type='text'
                    searchEndpoint={searchEndpoint}
                    searchColumns={searchColumns as any}
                    disabled={disabled}
                    columnDisplay={searchDisplayField as any}
                    displayRelatedField={displayRelatedField as any}
                    value={field.value ?? value ?? ''}
                    onValueChange={nextValue => {
                      field.onChange(nextValue);
                      onValueChange?.(nextValue);
                    }}
                    error={fieldError}
                    errorDisplay='none'
                    className={className}
                  />
                );
              }

              switch (type) {
                case 'table':
                  return (
                    <ErpInputTableV2
                      rows={field.value ? field.value : []}
                      columns={columns || []}
                      actionsDisabled={disabled}
                    />
                  );
                case 'number-formatted': {
                  const currentValue = value !== undefined ? value : field.value !== undefined ? field.value : '';

                  return (
                    <Input
                      type='text'
                      id={fieldId}
                      disabled={disabled}
                      value={isFocused ? displayValue : formatMoney(currentValue as any)}
                      onFocus={() => {
                        setIsFocused(true);
                        setDisplayValue(
                          currentValue !== undefined && currentValue !== null ? String(currentValue) : ''
                        );
                      }}
                      onBlur={event => {
                        field.onBlur();
                        onBlur?.(event as any);
                        setIsFocused(false);
                        const numericValue = parseNumber(event.target.value);
                        if (onValueChange) {
                          onValueChange(numericValue as any);
                        } else {
                          field.onChange(numericValue as any);
                        }
                      }}
                      onChange={event => {
                        setDisplayValue(event.target.value);
                        const parsedValue = parseNumber(event.target.value);
                        if (onValueChange) {
                          onValueChange(parsedValue as any);
                        } else {
                          field.onChange(parsedValue as any);
                        }
                      }}
                      placeholder={disabled ? undefined : placeholder}
                      aria-invalid={!!fieldError}
                      className={cn(
                        baseInputClassName,
                        'text-right',
                        disabled && disabledFieldClassName,
                        fieldError && errorFieldClassName,
                        className
                      )}
                    />
                  );
                }
                case 'select':
                  return (
                    <SelectFieldV2
                      fieldId={fieldId}
                      field={field}
                      options={options}
                      disabled={disabled}
                      className={className}
                      placeholder={placeholder}
                      baseInputClassName={baseInputClassName}
                      disabledFieldClassName={disabledFieldClassName}
                      errorClassName={fieldError ? errorFieldClassName : undefined}
                      invalid={!!fieldError}
                      resolveOptionValue={resolveOptionValue}
                      onValueChange={onValueChange}
                    />
                  );
                case 'checkbox':
                  return (
                    <div className='flex items-center space-x-2'>
                      <Checkbox
                        id={fieldId}
                        checked={field.value === undefined ? !!value : !!field.value}
                        onCheckedChange={checked => {
                          field.onChange(checked);
                          onValueChange?.(checked as any);
                        }}
                        disabled={disabled}
                        aria-invalid={!!fieldError}
                        className={cn('size-4 rounded-sm border-gray-300', fieldError && 'border-red-500')}
                      />
                      <Label htmlFor={fieldId} className={cn('text-sm text-black', labelClassName)}>
                        {label}
                      </Label>
                    </div>
                  );
                case 'date': {
                  const currentValue = field.value !== undefined ? field.value : value;

                  return (
                    <FormDatePickerV2
                      id={fieldId}
                      value={currentValue}
                      disabled={disabled}
                      invalid={!!fieldError}
                      placeholder={placeholder}
                      inputClassName={cn(fieldError && errorFieldClassName, className)}
                      onBlur={event => {
                        field.onBlur();
                        onBlur?.(event as any);
                      }}
                      onValueChange={nextValue => {
                        field.onChange(nextValue);
                        onValueChange?.(nextValue as any);
                      }}
                    />
                  );
                }
                case 'multiselect':
                  const selectedValues = Array.isArray(field.value) ? field.value : [];

                  return (
                    <div className='flex w-full flex-col gap-1'>
                      {selectedValues.length > 0 ? (
                        <div className='flex min-h-6 flex-wrap gap-1'>
                          {selectedValues.map(selectedValue => {
                            const selectedLabel =
                              options?.find(option => String(option.value) === String(selectedValue))?.label ??
                              String(selectedValue);
                            return (
                              <span
                                key={String(selectedValue)}
                                className='inline-flex h-5 max-w-full items-center truncate rounded bg-gray-100 px-2 text-xs text-gray-700'
                              >
                                {selectedLabel}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        placeholder && <span className='text-sm text-gray-400'>{placeholder}</span>
                      )}
                      <select
                        value={selectedValues.map(String)}
                        disabled={disabled}
                        multiple
                        id={fieldId}
                        aria-invalid={!!fieldError}
                        onChange={event => {
                          const resolvedValue = Array.from(event.target.selectedOptions, option =>
                            resolveOptionValue(option.value)
                          );
                          field.onChange(resolvedValue);
                          onValueChange?.(resolvedValue as any);
                        }}
                        className={cn(
                          baseInputClassName,
                          'min-h-[var(--erp-control-height)] border border-gray-200 focus-visible:border-main',
                          disabled && disabledFieldClassName,
                          fieldError && 'border-red-500 focus-visible:border-red-600',
                          className
                        )}
                      >
                        {options?.map((option: FormFieldV2Option, index: number) => (
                          <option key={getOptionKey(option, index)} value={String(option.value)}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                default: {
                  const currentValue = value !== undefined ? value : field.value !== undefined ? field.value : '';
                  const isNumberType = type === 'number';

                  return (
                    <Input
                      id={fieldId}
                      type={isNumberType ? 'text' : type}
                      disabled={disabled}
                      value={
                        isNumberType
                          ? isFocused
                            ? displayValue
                            : currentValue !== undefined && currentValue !== null
                              ? String(currentValue)
                              : ''
                          : currentValue !== undefined && currentValue !== null
                            ? String(currentValue)
                            : ''
                      }
                      onFocus={() => {
                        if (isNumberType) {
                          setIsFocused(true);
                          setDisplayValue(currentValue === 0 || currentValue === '0' ? '' : String(currentValue || ''));
                        }
                      }}
                      onBlur={event => {
                        field.onBlur();
                        onBlur?.(event as any);
                        if (isNumberType) {
                          setIsFocused(false);
                          const numericValue = parseNumber(event.target.value);
                          const transformedValue = transform
                            ? transform(numericValue !== null ? String(numericValue) : '')
                            : numericValue;
                          if (onValueChange) {
                            onValueChange(transformedValue as any);
                          } else {
                            field.onChange(transformedValue);
                          }
                        }
                      }}
                      onChange={event => {
                        const inputValue = event.target.value;
                        if (isNumberType) {
                          setDisplayValue(inputValue);
                          const parsedValue = parseNumber(inputValue);
                          if (onValueChange) {
                            onValueChange(parsedValue as any);
                          } else {
                            field.onChange(parsedValue);
                          }
                          return;
                        }

                        const transformedValue = transform ? transform(inputValue) : inputValue;
                        if (onValueChange) {
                          onValueChange(transformedValue as any);
                        } else {
                          field.onChange(transformedValue);
                        }
                      }}
                      placeholder={disabled ? undefined : placeholder}
                      aria-invalid={!!fieldError}
                      className={cn(
                        baseInputClassName,
                        isNumberType && 'text-right',
                        disabled && disabledFieldClassName,
                        fieldError && errorFieldClassName,
                        className
                      )}
                    />
                  );
                }
              }
            }}
          />
        </div>
      </div>

      {fieldError && <span className='mt-1 block text-xs text-red-500'>{fieldError}</span>}
    </div>
  );
};

export type { FormFieldV2Props } from './types';
